import { Typography } from "@mui/material";
import { _CO, _MONTHS } from "../../../utils/constants";
import { createColumn } from "../../../utils/function";
import { SUB_THEMES, THEME_SALE_BLK, TOTAL_NAME } from "./AgGridSalesBlock";
import {
  calculateExternalVolumes,
  calculateInternalVolumes,
  getPcRateIncrease,
  getRowDataNumber,
  updateNodeDataAndReturn,
} from "./utils";
import { currencyFormatter } from "../../../utils/AgGridUtils/ColumnBuilders/createColumn";

export const ColumnBuilder = (inputBlockData = {}, rateIncreaseData = []) => {
  const buildColumns = () => {
    const generatedColumns = [];

    generatedColumns.push(
      ...createColumn([
        {
          headerName: "",
          field: "theme",
          rowGroup: true,
          hide: true,
          cellRenderer: (params) => {
            return params.data?.theme;
          },
          suppressCount: true,
        },
        {
          headerName: "",
          field: "subThemes",
          flex: 2,
          cellRenderer: (params) => {
            const { node } = params;
            if (node.footer) {
              switch (node.key) {
                case THEME_SALE_BLK.DEPOT:
                  return TOTAL_NAME.QUOTE_PART;
                case THEME_SALE_BLK.TURNOVER:
                  return TOTAL_NAME.TOTAL_TURN_OVER;
                default:
                  return TOTAL_NAME.TOTAL_QTY;
              }
            }

            return params.data?.subThemes;
          },
        },
      ]),
    );

    for (let i = 1; i <= 12; i++) {
      const fiscalMonthIndex = _CO[i];

      generatedColumns.push(
        ...createColumn([
          {
            headerName: _MONTHS[fiscalMonthIndex],
            field: `${fiscalMonthIndex}`,
            editable: (p) => p.data?.theme === THEME_SALE_BLK.INPUT_BLK_MONTH,
            headerClass: "month-header",
            aggFunc: "sum",
            flex: 1,
            type: "rightAligned",
            valueParser: (params) =>
              parseFloat(params.newValue.replace(/\s/g, "")),
            valueFormatter: (params) => {
              return currencyFormatter(params.value, "");
            },
            valueGetter: (params) => {
              const { api, data } = params;
              if (!data) return 0;

              const fiscalMonthIndex = params.colDef.field;

              /******************************************************************************
               * SECTION: VOLUMES EXTERNES
               *****************************************************************************/

              if (data.subThemes === SUB_THEMES.EXT_VOL) {
                const externalVolumes = calculateExternalVolumes(
                  api,
                  fiscalMonthIndex,
                );
                if (externalVolumes === null) return null;

                return updateNodeDataAndReturn(
                  api,
                  SUB_THEMES.EXT_VOL,
                  fiscalMonthIndex,
                  externalVolumes,
                );
              }

              /******************************************************************************
               * FIN SECTION: VOLUMES EXTERNES
               *****************************************************************************/

              /******************************************************************************
               * SECTION: VOLUMES INTERNES
               *****************************************************************************/

              if (data.subThemes === SUB_THEMES.INT_VOL) {
                // Récupération des volumes internes depuis les données de base
                const internalVolumes = calculateInternalVolumes(
                  api,
                  fiscalMonthIndex,
                );

                if (internalVolumes === null) return null;

                // Mise à jour des données dans la grille et retour de la valeur
                return updateNodeDataAndReturn(
                  api,
                  SUB_THEMES.INT_VOL,
                  fiscalMonthIndex,
                  internalVolumes,
                );
              }

              /******************************************************************************
               * FIN SECTION: VOLUMES INTERNES
               *****************************************************************************/

              /******************************************************************************
               * SECTION: CALCUL DES VALEURS (TURNOVER)
               *****************************************************************************/
              if (data.theme === THEME_SALE_BLK.TURNOVER) {
                switch (data.subThemes) {
                  case SUB_THEMES.EXT_VALUES:
                    // Get the external volumes previously calculated
                    const externalVolumes = getRowDataNumber(
                      SUB_THEMES.EXT_VOL,
                      fiscalMonthIndex,
                      api,
                    );

                    if (externalVolumes === null) return null;

                    // Get the rate increase for this month
                    const pcRateIncrease = getPcRateIncrease(
                      rateIncreaseData || [],
                      fiscalMonthIndex,
                    );

                    // Apply the price and rate increase to the volumes
                    const externalPrice = inputBlockData?.externalPrice || 0;
                    const externalValue = externalPrice
                      ? Math.ceil(
                          externalVolumes *
                            externalPrice *
                            (1 + pcRateIncrease / 100),
                        )
                      : 0;

                    // Update the data in the grid and return the value
                    return updateNodeDataAndReturn(
                      api,
                      SUB_THEMES.EXT_VALUES,
                      fiscalMonthIndex,
                      externalValue,
                    );

                  case SUB_THEMES.INT_VALUES:
                    const internalVolumes = getRowDataNumber(
                      SUB_THEMES.INT_VOL,
                      fiscalMonthIndex,
                      api,
                    );

                    if (internalVolumes === null) return null;

                    const pcRateIncreaseInt = getPcRateIncrease(
                      rateIncreaseData || [],
                      fiscalMonthIndex,
                    );

                    const internalPrice = inputBlockData?.internalPrice || 0;
                    const internalValue = internalPrice
                      ? Math.ceil(
                          internalVolumes *
                            internalPrice *
                            (1 + pcRateIncreaseInt / 100),
                        )
                      : 0;

                    return updateNodeDataAndReturn(
                      api,
                      SUB_THEMES.INT_VALUES,
                      fiscalMonthIndex,
                      internalValue,
                    );

                  default:
                    return null;
                }
              }

              /******************************************************************************
               *  // FIN SECTION: CALCUL DES VALEURS (TURNOVER)
               *****************************************************************************/

              return Number(data?.[fiscalMonthIndex] || 0);
            },
          },
        ]),
      );
    }

    generatedColumns.push(
      ...createColumn([
        {
          headerName: "TOTAL",
          field: "total",
          editable: false,
          headerClass: "total-header-col",
          aggFunc: "sum",
          flex: 1,
          valueGetter: (params) => {
            const { data } = params;

            if (!data) return 0;

            return Array.from({ length: 12 }, (_, i) => i + 1).reduce(
              (sum, month) => sum + (Number(data[month]) || 0),
              0,
            );
          },
          cellRenderer: (params) => (
            <Typography variant="body2">
              {currencyFormatter(params.value, "")}
            </Typography>
          ),
        },
      ]),
    );

    return generatedColumns;
  };

  return buildColumns();
};
